import * as React from 'react';
import { TShippingForm } from './ShippingForm.types';
import './ShippingForm.styled.scss';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { TShippingFormData, TDeliveryMethod } from '../../Checkout.types';
import { DELIVERY_METHODS } from '../../Checkout.constants';
import { DeliveryMethodSelector } from '../DeliveryMethodSelector/DeliveryMethodSelector';
import { shippingSchema } from './ShippingForm.schema';

export const ShippingForm: TShippingForm = ({
  initialData,
  onContinue,
  onBack,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<TShippingFormData>({
    resolver: yupResolver(shippingSchema) as Resolver<TShippingFormData>,
    defaultValues: initialData || {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      street: '',
      buildingNumber: '',
      apartmentNumber: '',
      city: '',
      postalCode: '',
      deliveryMethod: 'inpost',
    },
  });

  const selectedDeliveryMethod = watch('deliveryMethod');
  const postalCode = watch('postalCode');
  const selectedDeliveryPoint = watch('deliveryPointName');
  const [inPostDialogOpen, setInPostDialogOpen] = React.useState(false);

  const onSubmit = (data: TShippingFormData) => {
    onContinue(data as TShippingFormData);
  };

  const handleInPostSelect = (pointId: string, pointName: string) => {
    setValue('deliveryPointId', pointId);
    setValue('deliveryPointName', pointName);
  };

  const handleDeliveryMethodChange = (method: TDeliveryMethod) => {
    // Reset delivery point when changing method
    if (method !== 'inpost') {
      // You can add logic here to clear delivery point fields
    }
  };

  return (
    <div className="shipping-form">
      <div className="shipping-form__header">
        <h6 className="shipping-form__title">Shipping Information</h6>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="shipping-form__form"
        noValidate
      >
        <div className="shipping-form__section">
          <h6 className="shipping-form__section-title">Contact Information</h6>
          <div className="shipping-form__row">
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Imię"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  required
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nazwisko"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  required
                />
              )}
            />
          </div>
          <div className="shipping-form__row">
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  required
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Numer telefonu"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message || 'Format: 123456789'}
                  required
                />
              )}
            />
          </div>
        </div>

        <Divider className="shipping-form__divider" />

        <div className="shipping-form__section">
          <h6 className="shipping-form__section-title">Address</h6>
          <div className="shipping-form__row">
            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Ulica"
                  fullWidth
                  error={!!errors.street}
                  helperText={errors.street?.message}
                  required
                />
              )}
            />
            <Controller
              name="buildingNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Numer budynku"
                  fullWidth
                  error={!!errors.buildingNumber}
                  helperText={errors.buildingNumber?.message}
                  required
                />
              )}
            />
            <Controller
              name="apartmentNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Numer mieszkania (opcjonalnie)"
                  fullWidth
                  error={!!errors.apartmentNumber}
                  helperText={errors.apartmentNumber?.message}
                />
              )}
            />
          </div>
          <div className="shipping-form__row">
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Miasto"
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  required
                />
              )}
            />
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Kod pocztowy"
                  fullWidth
                  error={!!errors.postalCode}
                  helperText={errors.postalCode?.message || 'Format: XX-XXX'}
                  required
                />
              )}
            />
          </div>
        </div>

        <Divider className="shipping-form__divider" />

        <div className="shipping-form__section">
          <h6 className="shipping-form__section-title">Delivery Method</h6>
          <Controller
            name="deliveryMethod"
            control={control}
            render={({ field }) => (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleDeliveryMethodChange(
                      e.target.value as TDeliveryMethod
                    );
                  }}
                >
                  {DELIVERY_METHODS.map((method) => (
                    <Card
                      key={method.id}
                      className={`shipping-form__delivery-card ${
                        selectedDeliveryMethod === method.id
                          ? 'shipping-form__delivery-card--selected'
                          : ''
                      }`}
                    >
                      <CardContent>
                        <FormControlLabel
                          value={method.id}
                          control={<Radio />}
                          sx={{
                            width: '100%',
                            '& .MuiFormControlLabel-label': {
                              width: '100%',
                            },
                          }}
                          label={
                            <div className="shipping-form__delivery-info">
                              <div className="shipping-form__delivery-header">
                                <h6>{method.name}</h6>
                                <h6>
                                  {method.price === 0
                                    ? 'Darmowa'
                                    : `${method.price} zł`}
                                </h6>
                              </div>
                              <p className="body2">{method.description}</p>
                              <p className="caption">
                                Szacowany czas: {method.estimatedDays}
                              </p>
                            </div>
                          }
                        />
                      </CardContent>
                    </Card>
                  ))}
                </RadioGroup>
                {errors.deliveryMethod && (
                  <p className="caption">{errors.deliveryMethod.message}</p>
                )}
              </FormControl>
            )}
          />

          {selectedDeliveryMethod === 'inpost' && (
            <div className="shipping-form__inpost-section">
              {selectedDeliveryPoint ? (
                <div className="shipping-form__inpost-selected">
                  <p className="body2">Wybrany paczkomat:</p>
                  <p className="shipping-form__inpost-selected-name">
                    {selectedDeliveryPoint}
                  </p>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setValue('deliveryPointId', '');
                      setValue('deliveryPointName', '');
                    }}
                    className="shipping-form__inpost-change-button"
                  >
                    Zmień
                  </Button>
                </div>
              ) : (
                <>
                  <div className="shipping-form__inpost-note">
                    <p className="body2">
                      Wybierz paczkomat InPost dla dostawy
                    </p>
                  </div>
                  <Button
                    variant="outlined"
                    onClick={() => setInPostDialogOpen(true)}
                    className="shipping-form__inpost-button"
                  >
                    Wybierz Paczkomat
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <DeliveryMethodSelector
          open={inPostDialogOpen}
          onClose={() => setInPostDialogOpen(false)}
          onSelect={handleInPostSelect}
          deliveryMethod={selectedDeliveryMethod}
          postalCode={postalCode}
        />

        <div className="shipping-form__actions">
          <Button
            variant="outlined"
            onClick={onBack}
            className="shipping-form__back-button"
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            className="shipping-form__continue-button"
          >
            Continue to Payment
          </Button>
        </div>
      </form>
    </div>
  );
};
